/**
 * EGY LEFOTÓZOTT LAP, TELJES KÉPERNYŐN (#87).
 *
 * Az áttekintő bélyegképe 56 px magas — azon a kis betűs sorok, a számlaszám
 * vagy egy elmosódott összeg nem ellenőrizhető. Viktor telefonos próbája:
 * „nem bírom megnyitni, hogy ellenőrizzem, amit fényképeztem".
 *
 * ═══ KÉT ÁLLAPOT, EGY KOPPINTÁS ═══
 *
 * Alapból az EGÉSZ lap látszik (ráfér a képernyőre). Koppintásra a kép a
 * saját felbontásán nyílik, és ujjal görgethető — így olvasható az apró
 * betű. Még egy koppintás, és újra az egész lap. A csípő-nagyítás a böngésző
 * saját gesztusa marad; ez a kettő a biztos út, ami minden telefonon megy.
 *
 * Réteg az áttekintő fölött, nem útvonal: bezárva az áttekintő ott áll,
 * ahol volt.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trapTab } from '@/lib/focus-trap';

interface PagePreviewProps {
  /** A lap előnézeti URL-je (`URL.createObjectURL`) — a sorozat kezeli. */
  src: string;
  /** „1. oldal" — a fejléc és a kép neve. */
  title: string;
  onClose: () => void;
}

export function PagePreview({ src, title, onClose }: PagePreviewProps) {
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(false);
  const titleId = useId();
  const close = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // A FÓKUSZ A BEZÁRÓ GOMBRA — a billentyűzet és a képernyőolvasó innen
  // indul, és az Escape is innen zár.
  useEffect(() => {
    close.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab' && panel.current) {
        // A TAB A NÉZETEN MARAD — az áttekintő gombjai alatta vannak.
        trapTab(event, panel.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={panel}
      className="page-preview"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <header className="page-preview-head">
        <h2 id={titleId}>{title}</h2>
        <button ref={close} type="button" className="page-preview-close" onClick={onClose}>
          {t('capture.preview.close')}
        </button>
      </header>

      <div className={`page-preview-stage${zoomed ? ' is-zoomed' : ''}`}>
        <button
          type="button"
          className="page-preview-toggle"
          aria-pressed={zoomed}
          aria-label={t(zoomed ? 'capture.preview.zoomOut' : 'capture.preview.zoomIn')}
          onClick={() => setZoomed((value) => !value)}
        >
          <img src={src} alt={title} />
        </button>
      </div>

      <p className="page-preview-hint">
        {t(zoomed ? 'capture.preview.zoomOut' : 'capture.preview.zoomIn')}
      </p>
    </div>
  );
}
